<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const router = useRouter();

// Variables for client data and search functionality
const clients = ref([]);
const temp_clients = ref([]);
const next = ref(null);
const temp_next = ref(null);
const previous = ref(null);
const temp_previous = ref(null);
const count = ref(0);
const temp_count = ref(0)
const currentPage = ref(1);
const pageSize = 20;
const search_is_active = ref(false);
const filter_is_active = ref(false);
// Initialize areas_streets_array and client_types as empty arrays
const areas_streets_array = ref([]);
const client_types = ref([]);

// Search input text
const search_text = ref('');
const search_url = 'search_client/';
const is_search_activated = ref(false);
const is_filter_on = ref()
const formData = ref({
  selected_area: '',
  selected_street: '',
  selected_client_type: ''
});

// Function to fetch clients without search
async function fetchClients(limit = 20, offset = 0) {
  try {
    const response = await axios.get(`clients/?limit=${limit}&offset=${offset}`);

    if (response.data.results) {
      clients.value = response.data.results;
      temp_clients.value = clients.value;
      next.value = response.data.next;
      temp_next.value = next.value;
      previous.value = response.data.previous;
      temp_previous.value = previous.value;
      count.value = response.data.count;
      temp_count.value = count.value;
    }
  } catch (err) {
    console.error('Error fetching clients:', err);
  }
}

// Function to fetch filter fields
async function fetchFilterFields() {
  try {
    const response = await axios.get('areas_with_streets_list/');
    areas_streets_array.value = response.data;
    areas_streets_array.value.unshift({ area: '', streets: [''] });


    const clientResponse = await axios.get('client_types_list/');
    client_types.value = clientResponse.data;
    client_types.value.unshift({ id: '', name: '' });
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}

// Function to search clients
async function searchClients(limit = 20, offset = 0) {
  if (!search_text.value) {
    resetSearch();
    return;
  }

  try {
    currentPage.value = offset+1;
    const search_paginated_url = `${search_url}?limit=${limit}&offset=${offset}`;
    const res = await axios.get(search_paginated_url, {
      params: { q: search_text.value }
    });
    if (res.data) {
      search_is_active.value = true;
      temp_clients.value = res.data.results;
      temp_next.value = res.data.next;
      temp_previous.value = res.data.previous;
      temp_count.value = res.data.count;
      // Update currentPage only if it's a new search
      // currentPage.value = offset / limit + 1;
    }
    is_search_activated.value = true;
  } catch (err) {
    console.error('Error searching clients:', err);
  }
}

function resetSearch() {
  temp_clients.value = clients.value;
  temp_next.value = null;
  temp_previous.value = null;
  temp_count.value = clients.value.length;
  currentPage.value = 1; // Resetting to 1 if search is cleared
  search_is_active.value = false;
}

// Function to filter clients based on selected filters
async function clientFilter(limit = 20, offset = 0) {
  try {
    const res = await axios.get(`client_filter/?limit=${limit}&offset=${offset}`, {
      params: {
        client_type: formData.value.selected_client_type,
        area_id: formData.value.selected_area.area_id,
        street_id: formData.value.selected_street.street_id
      }
    });
    is_filter_on.value = true;
    temp_clients.value = res.data.results;
    temp_next.value = res.data.next;
    temp_previous.value = res.data.previous;
    temp_count.value = res.data.count;
    if (!filter_is_active.value) {
      currentPage.value = 1;
      filter_is_active.value = true;
    }

  } catch (err) {
    console.error('Error filtering clients:', err);
  }
}

function resetResults(){
  is_search_activated.value = false;
  is_filter_on.value = false;
  temp_clients.value = clients.value;
  search_text.value = '';
  formData.value = {
    selected_area: '',
    selected_street: '',
    selected_client_type: ''
  }
  temp_next.value = next.value;
  temp_previous.value = previous.value;
  temp_count.value = count.value;
  currentPage.value = 1;
  filter_is_active.value = false;
}

// Function to navigate to client info page
function goToClientInfo(client_id) {
  router.push({ path: '/client', name: 'client information', query: { client_id } });
}

// Pagination handlers
async function goToNextPage() {
  if (temp_next.value) {
    const nextPage = currentPage.value + 1;
    const offset = (nextPage - 1) * pageSize;

    if (search_is_active.value) {
      await searchClients(pageSize, offset);
    } else if (filter_is_active.value) {
      await clientFilter(pageSize, offset);
    } else {
      await fetchClients(pageSize, offset);
    }

    // Update currentPage only if the API call is successful
    currentPage.value = nextPage;
  }
}

async function goToPreviousPage() {
  if (temp_previous.value) {
    const prevPage = currentPage.value - 1;
    const offset = (prevPage - 1) * pageSize;

    if (search_is_active.value) {
      await searchClients(pageSize, offset);
    } else if (filter_is_active.value) {
      await clientFilter(pageSize, offset);
    } else {
      await fetchClients(pageSize, offset);
    }

    // Update currentPage only if the API call is successful
    currentPage.value = prevPage;
  }
}

// On mount, fetch clients data initially
onMounted(() => {
  fetchClients();
  fetchFilterFields();
});
</script>


<template>
  <div class="content-wrapper mt-5 pt-3">
    <div class="">
      <!-- Search Input and Buttons -->
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <!-- Left Side: Buttons -->
        <div class="d-flex align-items-center gap-2 flex-wrap">

          <div class="dropdown">
            <button
                class="btn btn-secondary px-3 fw-semibold d-flex align-items-center ms-1"
                type="button"
                data-bs-toggle="dropdown"
                id="dropdownMenuButton"
            >
              <i class="bi bi-filter me-1"></i> Фильтры
            </button>

            <div class="dropdown-menu filter-menu p-3" aria-labelledby="dropdownMenuButton">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">Кол-во: {{ temp_count }}</span>
              </div>
              <!-- Filter Form -->
              <div class="" id="filterForm">
                <div class="row g-3">
                  <div class="col-12 col-md-3">
                    <label class="form-label">Тип абонента:</label>
                    <select class="form-select" v-model="formData.selected_client_type">
                      <option v-for="type in client_types" :value="type.id" :key="type.id">{{ type.name }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-3">
                    <label class="form-label">Участок:</label>
                    <select class="form-select" v-model="formData.selected_area">
                      <option v-for="area in areas_streets_array" :value="area">{{ area.area_name }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-3" :class="{'fade': !formData.selected_area}">
                    <label class="form-label">Улица:</label>
                    <select class="form-select" v-model="formData.selected_street">
                      <option v-if="formData.selected_area" v-for="(street, index) in formData.selected_area.streets" :key="index" :value="street">{{ street.street_name }}</option>
                      <option v-else></option>
                    </select>
                  </div>
                  <div class="col-12 col-md-3 d-flex align-items-end">
                    <button type="submit" class="btn btn-primary w-100" :class="!((formData.selected_client_type && !formData.selected_area) || (formData.selected_area && formData.selected_street)) ? 'disabled' : ''" @click.prevent="clientFilter">
                      Применить
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
          <button v-if="search_text || is_search_activated || is_filter_on" class="btn btn-danger d-flex align-items-center px-3" @click.prevent="resetResults">
            <i class="bi bi-x-circle me-1"></i> Сбросить
          </button>
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <ul class="pagination shadow-sm mb-0">
            <li class="page-item" :class="temp_previous ? '' : 'disabled'" @click="goToPreviousPage">
              <a class="page-link" href="#" aria-label="Previous">
                <i class="fas fa-solid fa-arrow-left"></i>
              </a>
            </li>
            <li class="page-item">
              <span class="page-link">Page {{ currentPage }} of {{ Math.ceil(temp_count / pageSize) }}</span>
            </li>
            <li class="page-item" :class="temp_next ? '' : 'disabled'" @click="goToNextPage">
              <a class="page-link" href="#" aria-label="Next">
                <i class="fas fa-solid fa-arrow-right"></i>
              </a>
            </li>
          </ul>
        </div>
        <!-- Right Side: Search Input -->
        <div class="input-group flex-grow-1 flex-md-grow-0 col-12 col-md-4">
          <span class="input-group-text bg-light">
            <i class="bi bi-search text-muted"></i>
          </span>
          <input type="text" @input="searchClients" v-model="search_text" @keyup.enter="searchClients" class="form-control" placeholder="Поиск абонента">
        </div>
      </div>
      <!-- Client Table -->
      <div class="table-responsive">
        <table class="table table-hover table-bordered table-striped">
          <thead class="table-light">
          <tr>
            <th scope="col" class="col-2">ИИН</th>
            <th scope="col" class="col-6">ФИО</th>
            <th scope="col" class="col-3 col-sm-5">Телефон</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="client in temp_clients" :key="client.id" @click="goToClientInfo(client.id)" class="cursor-pointer">
            <td>{{ client.username }}</td>
            <td>{{ client.lastname }} {{ client.firstname }} {{ client.middlename }}</td>
            <td>{{ client.phonenumber }}</td>
          </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>
<style scoped>
.filter-menu{
  width: 800px;
}
@media only screen and (max-width: 600px){
  .filter-menu{
    width: 350px;
  }
}
</style>